import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@et/environment';
import {
  AddEjournalSignerSignatureDto,
  AddEtJournalEntryDto,
  AddEtJournalSignerDto,
  AddPacketJournalDto,
  EjournalEntry,
  EjournalEntryDto,
  EjournalResponse,
  EjournalSignerSignature,
  EtJournalEntryWithId,
  EtJournalListEntry,
  EtjournalPacketEntry,
  EtjournalPacketEntryWithEntries,
  EtJournalSignerWithId,
  GenerateEjournalDto,
  PatchEjournalSignerSignatureDto,
  PatchEtJournalSignerDto,
} from '@et/typings';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const EJOURNALS_URL = '/api/Ejournals';

/**
 * Query params for fetching eJournal entries by profile with pagination, sort and filter.
 * Backend is expected to support these query parameters on GET /api/Ejournals/profile.
 */
export interface EtJournalProfileParams {
  profileId: string;
  packetId?: string;
  state?: string;
  sortBy?: string;
  isSortAscending?: boolean;
  page?: number;
  pageSize?: number;
  timeStart?: string;
  timeEnd?: string;
  localTime?: string;
}

/**
 * API service for eJournal operations against the profile backend.
 * POST /api/Ejournals sends an array of eJournal entries.
 * Used by both native-documents and profile (web) flows.
 */
@Injectable({
  providedIn: 'root',
})
export class EjournalApiService {
  private http = inject(HttpClient);

  private readonly baseUrl = environment.profilev2.domain + EJOURNALS_URL;

  /**
   * Generates an eJournal.
   * @param journalPayload - The payload for the eJournal.
   * @returns Observable of the HTTP response.
   */
  generateEjournal(
    payload: GenerateEjournalDto,
  ): Observable<EtjournalPacketEntryWithEntries> {
    return this.http
      .post<EjournalResponse<EtjournalPacketEntryWithEntries>>(
        `${this.baseUrl}/autoPopulate`,
        payload,
      )
      .pipe(map((response) => response.data));
  }

  /**
   * Finalizes an eJournal.
   * @param eJournalId - The id of the eJournal to finalize.
   * @returns Observable of the HTTP response.
   */
  finalizeEjournal(eJournalId: string): Observable<string> {
    const params = new HttpParams().set('journalId', eJournalId);
    return this.http
      .post<EjournalResponse<string>>(
        `${this.baseUrl}/finalize`,
        {},
        { params },
      )
      .pipe(map((response) => response.data));
  }

  /**
   * Downloads the eJournal for the given packet id.
   * @param packetId - The id of the packet to export.
   * @returns Observable of the array buffer.
   */
  exportEjournalByPacketId(
    packetId: string,
    timeZone: string,
  ): Observable<ArrayBuffer> {
    const params = new HttpParams().set('localTime', timeZone);
    return this.http.post(
      `${this.baseUrl}/ExportEJournal/${packetId}`,
      {},
      { responseType: 'arraybuffer', params },
    );
  }

  /**
   * Exports eJournal search results or entries.
   * @param httpParams - The HTTP parameters.
   * @param type - The type of export.
   * @returns Observable of the array buffer.
   */
  exportEjournals(
    httpParams: EtJournalProfileParams,
    type: 'packets' | 'entries',
  ) {
    const url =
      type === 'packets' ? '/ExportEJournalSearch' : '/ExportEJournalEntries';
    const params = this.makeHttpParams(httpParams);
    return this.http.post(
      `${this.baseUrl}${url}`,
      {},
      { responseType: 'arraybuffer', params },
    );
  }

  /**
   * Posts eJournal entries to the profile service.
   * @param entries Array of eJournal entries per the API contract
   * @returns Observable of the HTTP response
   */
  postEjournals(entries: EjournalEntryDto[]): Observable<unknown> {
    return this.http.post<unknown>(this.baseUrl, entries);
  }

  /**
   * Adds a new packet journal entry.
   * @param packetJournal The packet journal entry to add
   * @returns Observable of the HTTP response
   */
  createPacketJournal(
    packetJournal: AddPacketJournalDto,
  ): Observable<EtjournalPacketEntry> {
    return this.http
      .post<EjournalResponse<EtjournalPacketEntry>>(
        `${this.baseUrl}/createPacketJournal`,
        packetJournal,
      )
      .pipe(map((response) => response.data));
  }

  /**
   * Updates an existing packet journal entry.
   * @param journalPacketId The ID of the packet journal entry to update
   * @param packetJournal The partial packet journal entry to update
   * @returns Observable of the HTTP response
   */
  patchPacketJournal(
    journalPacketId: string,
    packetJournal: Partial<EtjournalPacketEntry>,
  ): Observable<EtjournalPacketEntry> {
    const params = new HttpParams().set('JournalPacketId', journalPacketId);
    return this.http
      .patch<EjournalResponse<EtjournalPacketEntry>>(
        `${this.baseUrl}/updateJournalPacket`,
        packetJournal,
        {
          params,
        },
      )
      .pipe(map((response) => response.data));
  }

  /**
   * Patches an eJournal entry.
   * @param eJournalId - The ID of the eJournal to patch
   * @param entry - The partial eJournal entry to patch
   * @returns Observable of the HTTP response
   */
  patchEjournal(
    eJournalId: string,
    entry: Partial<EjournalEntryDto>,
  ): Observable<EjournalResponse<EjournalEntry>> {
    return this.http.patch<EjournalResponse<EjournalEntry>>(
      `${this.baseUrl}/${eJournalId}`,
      entry,
    );
  }

  /**
   * Adds a new signer to an eJournal.
   * @param signer The signer to add
   * @returns Observable of the HTTP response
   */
  addSigner(signer: AddEtJournalSignerDto): Observable<EtJournalSignerWithId> {
    return this.http
      .post<EjournalResponse<Array<EtJournalSignerWithId>>>(
        `${this.baseUrl}/createJournalSigners`,
        [signer],
      )
      .pipe(map((response) => response.data[0]));
  }

  /**
   * Deletes a signer from the eJournal.
   * @param signerId - The ID of the signer to delete.
   * @returns Observable of the HTTP response.
   */
  deleteSigner(signerId: string): Observable<unknown> {
    return this.http.delete<unknown>(`${this.baseUrl}/deleteSigners`, {
      body: [signerId],
    });
  }

  /**
   * Patches an existing signer.
   * @param signerId The ID of the signer to patch
   * @param signer The partial signer to patch
   * @returns Observable of the HTTP response
   */
  patchSigner(
    signerId: string,
    signer: Partial<PatchEtJournalSignerDto>,
  ): Observable<EtJournalSignerWithId> {
    const params = new HttpParams().set('signerId', signerId);
    return this.http
      .patch<EjournalResponse<EtJournalSignerWithId>>(
        `${this.baseUrl}/updateJournalSigner`,
        signer,
        { params },
      )
      .pipe(map((response) => response.data));
  }

  /**
   * Creates a signature for an eJournal signer.
   *
   * @param signature - Payload for the new signature.
   * @returns Observable emitting the created signature.
   */
  addSignerSignature(
    signature: AddEjournalSignerSignatureDto,
  ): Observable<EjournalSignerSignature> {
    return this.http
      .post<EjournalResponse<Array<EjournalSignerSignature>>>(
        `${this.baseUrl}/createJournalSignatures`,
        [signature],
      )
      .pipe(map((response) => response.data[0]));
  }

  /**
   * Updates an existing signer signature.
   *
   * @param signatureId - Id of the signature to update.
   * @param signature - Partial signature fields to patch.
   * @returns Observable emitting the updated signature.
   */
  updateSignerSignature(
    signatureId: string,
    signature: PatchEjournalSignerSignatureDto,
  ): Observable<EjournalSignerSignature> {
    const params = new HttpParams().set('signatureId', signatureId);
    return this.http
      .patch<EjournalResponse<EjournalSignerSignature>>(
        `${this.baseUrl}/updateJournalSignature`,
        signature,
        { params },
      )
      .pipe(map((response) => response.data));
  }

  /**
   * Deletes a signer signature by id.
   *
   * @param signatureId - Id of the signature to delete.
   * @returns Observable of the HTTP response body.
   */
  deleteSignerSignature(signatureId: string): Observable<unknown> {
    return this.http.delete<unknown>(`${this.baseUrl}/deleteSignatures`, {
      body: [signatureId],
    });
  }

  /**
   * Creates a journal entry for an eJournal.
   *
   * @param entry - Payload for the new entry.
   * @returns Observable emitting the created entry (with server-assigned `id`).
   */
  addEjournalEntry(
    entry: AddEtJournalEntryDto,
  ): Observable<EtJournalEntryWithId> {
    return this.addEjournalEntries([entry]).pipe(map((entries) => entries[0]));
  }

  /**
   * Creates one or more journal entries in a single request.
   *
   * @param entries - Payloads for the new entries.
   * @returns Observable emitting created entries (with server-assigned `id`s).
   */
  addEjournalEntries(
    entries: AddEtJournalEntryDto[],
  ): Observable<EtJournalEntryWithId[]> {
    return this.http
      .post<EjournalResponse<Array<EtJournalEntryWithId>>>(
        `${this.baseUrl}/createJournalEntries`,
        entries,
      )
      .pipe(map((response) => response.data ?? []));
  }

  /**
   * Adds a supplemental note to a journal entry.
   * @param entryId - Id of the entry to add the supplemental note to.
   * @param note - The supplemental note to add.
   * @returns Observable emitting the updated entry.
   */
  addSupplementalEntry(
    entryId: string,
    note: string,
  ): Observable<EtJournalEntryWithId> {
    const params = new HttpParams().set('entryId', entryId);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const body = JSON.stringify(note);
    return this.http
      .patch<EjournalResponse<EtJournalEntryWithId>>(
        `${this.baseUrl}/addSupplementalNote`,
        body,
        { params, headers },
      )
      .pipe(map((response) => response.data));
  }

  /**
   * Updates an existing journal entry.
   *
   * @param entryId - Id of the entry to update.
   * @param entry - Full entry payload including `id` matching the entry being patched.
   * @returns Observable emitting the updated entry.
   */
  patchEjournalEntry(
    entryId: string,
    entry: Partial<EtJournalEntryWithId>,
  ): Observable<EtJournalEntryWithId> {
    const params = new HttpParams().set('entryId', entryId);
    return this.http
      .patch<EjournalResponse<EtJournalEntryWithId>>(
        `${this.baseUrl}/updateJournalEntry`,
        entry,
        { params },
      )
      .pipe(map((response) => response.data));
  }

  /**
   * Deletes a single journal entry by id.
   *
   * @param entryId - Entry id to delete.
   * @returns Observable of the HTTP response.
   */
  deleteEntry(entryId: string): Observable<unknown> {
    return this.deleteEntries([entryId]);
  }

  /**
   * Deletes one or more journal entries by id.
   *
   * @param entryIds - Entry ids to delete.
   * @returns Observable of the HTTP response.
   */
  deleteEntries(entryIds: string[]): Observable<unknown> {
    return this.http.delete<unknown>(`${this.baseUrl}/deleteEntries`, {
      body: entryIds,
    });
  }

  /**
   * Gets eJournal entries by profile ID.
   * @param profileId The profile ID
   * @returns Observable of an array of eJournal entries
   */
  getEjournalsByProfile(profileId: string): Observable<EjournalEntryDto[]> {
    const params = new HttpParams().set('ProfileId', profileId);
    return this.http
      .get<{ data: EjournalEntryDto[] }>(`${this.baseUrl}/profile`, {
        params,
      })
      .pipe(
        map((response) => (Array.isArray(response.data) ? response.data : [])),
      );
  }

  /**
   * Gets a list of eJournal entries by profile and packet ID.
   * @param httpParams The HTTP parameters
   * @returns Observable of an array of eJournal entries
   */
  getEjournalsListByPackets(
    httpParams: EtJournalProfileParams,
  ): Observable<EtjournalPacketEntry[]> {
    const params = this.makeHttpParams(httpParams);
    return this.http
      .get<{ data: EtjournalPacketEntry[] }>(`${this.baseUrl}/profile`, {
        params,
      })
      .pipe(map((response) => response.data));
  }

  /**
   * Gets a list of eJournal entries by profile and entry ID.
   * @param httpParams The HTTP parameters
   * @returns Observable of an array of eJournal entries
   */
  getEjournalsListByEntries(
    httpParams: EtJournalProfileParams,
  ): Observable<EtJournalListEntry[]> {
    const params = this.makeHttpParams(httpParams);
    return this.http
      .get<{ data: EtJournalListEntry[] }>(`${this.baseUrl}/profile/entries`, {
        params,
      })
      .pipe(map((response) => response.data));
  }

  /**
   * Gets a full eJournal with signers and entries by journal id.
   *
   * @param journalId - eJournal (journal packet) id.
   * @returns Observable emitting the full journal payload.
   */
  getPacketEjournal(
    journalId: string,
  ): Observable<EtjournalPacketEntryWithEntries> {
    const params = new HttpParams().set('JournalPacketId', journalId);
    return this.http
      .get<EjournalResponse<EtjournalPacketEntryWithEntries>>(
        `${this.baseUrl}/fullJournal`,
        {
          params,
        },
      )
      .pipe(map((response) => response.data));
  }

  /**
   * Gets eJournal entries by profile ID and packet ID.
   * @param profileId The profile ID
   * @param packetId The packet ID
   * @returns Observable of an array of eJournal entries
   */
  getEjournalsByProfileAndPacket(
    profileId: string,
    packetId: string,
  ): Observable<EjournalEntryDto[]> {
    const params = new HttpParams()
      .set('ProfileId', profileId)
      .set('PacketId', packetId);
    return this.http
      .get<{ data: EjournalEntryDto[] }>(`${this.baseUrl}/profile`, {
        params,
      })
      .pipe(
        map((response) => (Array.isArray(response.data) ? response.data : [])),
      );
  }

  /**
   * Deletes one or more eJournal entries by id.
   *
   * @param eJournalIds - Entry ids to delete.
   * @returns Observable of the HTTP response.
   */
  deleteEjournal(eJournalIds: string[]): Observable<unknown> {
    return this.http.delete<unknown>(`${this.baseUrl}/deleteEntries`, {
      body: eJournalIds,
    });
  }

  /**
   * Deletes signers matching a name across all entries for a packet.
   *
   * @param packetId - Packet scope.
   * @param signerName - Signer name to match.
   * @returns Observable of the HTTP response.
   */
  deleteSignersFromAllPacketEntries(
    packetId: string,
    signerName: string,
  ): Observable<unknown> {
    const params = new HttpParams()
      .set('packetId', packetId)
      .set('signerName', signerName);
    return this.http.delete<unknown>(`${this.baseUrl}/deleteSignersByPacket`, {
      params,
    });
  }

  /**
   * Makes HTTP parameters from the EtJournalProfileParams.
   * @param params The EtJournalProfileParams
   * @returns The HTTP parameters
   */
  private makeHttpParams(params: EtJournalProfileParams): HttpParams {
    let httpParams = new HttpParams().set('ProfileId', params.profileId);
    if (params.packetId != null && params.packetId !== '') {
      httpParams = httpParams.set('PacketId', params.packetId);
    }
    if (params.state != null && params.state !== '') {
      httpParams = httpParams.set('State', params.state);
    }
    if (params.sortBy != null && params.sortBy !== '') {
      httpParams = httpParams.set('SortBy', params.sortBy);
    }
    if (params.isSortAscending != null) {
      httpParams = httpParams.set(
        'IsSortAscending',
        params.isSortAscending.toString(),
      );
    }
    if (params.page != null) {
      httpParams = httpParams.set('Page', params.page.toString());
    }
    if (params.pageSize != null) {
      httpParams = httpParams.set('PageSize', params.pageSize.toString());
    }
    if (params.timeStart != null && params.timeStart !== '') {
      httpParams = httpParams.set('TimeStart', params.timeStart);
    }
    if (params.timeEnd != null && params.timeEnd !== '') {
      httpParams = httpParams.set('TimeEnd', params.timeEnd);
    }
    if (params.localTime != null && params.localTime !== '') {
      httpParams = httpParams.set('LocalTime', params.localTime);
    }
    return httpParams;
  }
}
